<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useGamesStore, useGamesList, type CmsItem } from '@nsftx/seven-client-retail-vue-sgp'
import { useScreenOrientation } from '@nsftx/seven-client-retail-vue-common'
import {
  NXGDSNavigationPanel,
  NXGDSNavigationItem,
  NXGDSNavigationItemState,
  type NXGDSNavigationItemModel
} from '@nsftx/seven-nxg-design-system'
import router from '@/router'
import { useCmsConfigStore } from '@/stores/cms/cmsConfigStore'

const cmsStore = useCmsConfigStore()
const gamesStore = useGamesStore()
const { gameLinks } = storeToRefs(gamesStore)

const { orientation } = useScreenOrientation()
const { formattedGames } = useGamesList(gameLinks)

const collections = computed(() => {
  const collection = cmsStore.cmsConfig?.collections?.docs.map((collection) => ({
    ...collection,
    variants:
      collection.variants.find((variant) => variant.orientation === orientation.value) ??
      collection.variants[0]
  }))
  return collection
    ?.map((collection) => ({
      ...collection,
      variants: {
        ...collection.variants,
        items: collection.variants?.items.filter(
          (item) => item.item.relationTo === 'ssbt-game-tiles'
        )
      }
    }))
    .filter((collection) => collection.variants.items!.length! > 0)
})

const allCmsItems = computed(() => cmsPanels.value?.flatMap((panel) => panel.items) ?? [])

const gamesCollection = computed(() =>
  allCmsItems.value.filter((item, index) => {
    return allCmsItems.value.findIndex((i) => i.uuid === item.uuid) === index
  })
)

const classicGames = computed<NXGDSNavigationItemModel[]>(() =>
  formattedGames.value
    .filter((game) => {
      return !gamesCollection.value?.some((item) => item.uuid === game.linkCode)
    })
    .map((game) => ({
      uuid: game.linkCode,
      title: game.gameDisplayName,
      imageSrc: game.thumbnail,
      state: 'default',
      size: 'lg'
    }))
)

const handleClick = (item: NXGDSNavigationItemModel) => {
  if (item.state === NXGDSNavigationItemState.disabled) return

  router.push({ name: 'game', params: { linkCode: item.uuid } })
}

const columnCount = computed(() => (orientation.value === 'landscape' ? 4 : 2))
const grid = computed(() => `auto / repeat(${columnCount.value}, 1fr)`)

const cmsPanels = computed(() => {
  return collections.value
    ?.map((collection) => {
      const items: NXGDSNavigationItemModel[] = ((collection.variants.items as CmsItem[]) ?? [])
        .filter(
          (entry): entry is Extract<CmsItem, { item: { relationTo: 'ssbt-game-tiles' } }> =>
            entry.item.relationTo === 'ssbt-game-tiles'
        )
        .filter((entry) => {
          if (collection.showUnavailableGames) return true

          return gamesStore.isGameAvailable(entry.item.value.game)
        })
        .map((entry) => {
          const value = entry.item.value
          const displayId = value.game?.installation?.displayId
          const linkCode = value.game?.code ?? displayId ?? value.game?.id ?? value.title
          const formattedGame = value.game?.code
            ? formattedGames.value.find((game) => game.linkCode === value.game?.code)
            : undefined
          const available = gamesStore.isGameAvailable(value.game)

          return {
            uuid: linkCode,
            title: value.name,
            imageSrc: value.backgroundImage?.url ?? formattedGame?.thumbnail,
            state: available ? NXGDSNavigationItemState.default : NXGDSNavigationItemState.disabled,
            size: 'lg'
          }
        })

      return {
        id: collection.id,
        title: collection.title,
        items
      }
    })
    .filter((panel) => panel.items.length > 0)
})
</script>

<template>
  <main class="st-home-page">
    <div>
      <NXGDSNavigationPanel
        v-model="classicGames"
        title="Classics"
        :grid="grid"
        class="navigation-panel"
      >
        <template #item="{ item }">
          <NXGDSNavigationItem v-bind="item" @click="handleClick(item)" />
        </template>
      </NXGDSNavigationPanel>

      <NXGDSNavigationPanel
        v-for="panel in cmsPanels"
        :key="panel.id"
        v-model="panel.items"
        :title="panel.title"
        :grid="grid"
        class="navigation-panel"
      >
        <template #item="{ item }">
          <NXGDSNavigationItem v-bind="item" @click="handleClick(item)" />
        </template>
      </NXGDSNavigationPanel>
    </div>
  </main>
</template>

<style scoped lang="scss">
.st-home-page {
  padding: var(--nxgds-alias-spacing-2xl);
}

.navigation-panel {
  margin-bottom: var(--nxgds-alias-spacing-5xl);
  padding-inline: var(--nxgds-alias-spacing-7xl);
}
</style>
